import React, { useCallback, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import Icon from '../../../../atoms/Icon'
import Loading from '../../../../molecules/Loading'
import FlexRow from '../../../../molecules/FlexRow'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import useSfClientTasks from './useSfClientTasks'
import WoSfTaskDetailModal from './WoSfTaskDetailModal'
import { SeDebugProvider } from './SeDebugContext'
import SeDebugControl from './SeDebugControl'
import { getTaskStatus, formatTaskDate, applyFilters } from './helpers'

const SIZE_STYLES = {
  sm: { titleFontSize: '0.875rem', rowPadding: '8px 0', fontSize: 'var(--font-size-sm)', minHeight: '120px' },
  md: { titleFontSize: '1rem', rowPadding: '10px 0', fontSize: 'var(--font-size-sm)', minHeight: '180px' },
  lg: { titleFontSize: '1.25rem', rowPadding: '12px 0', fontSize: 'var(--font-size-md)', minHeight: '240px' }
}

const TASK_STATUS = {
  completed: { icon: 'checkCircle', color: 'var(--color-growth-500)' },
  overdue: { icon: 'clock', color: 'var(--color-decline-500)' },
  outstanding: { icon: 'radioUnchecked', color: 'var(--text-color-subtle)' }
}

function WoSfTaskRow ({ task, sizes, onClick }) {
  const status = getTaskStatus(task)
  const cfg = TASK_STATUS[status]
  const dueStr = formatTaskDate(task.dueDate)

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick() } }}
      style={{ borderBottom: '1px solid var(--color-neutral-200)', padding: sizes.rowPadding, cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          paddingTop: '2px',
          color: cfg.color
        }}
        >
          <Icon name={cfg.icon} customSize='20px' />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {task.isHighPriority ? (
            <div style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-decline-500)',
              fontWeight: 600
            }}
            >
              High Priority
            </div>
          ) : null}
          <div style={{
            fontSize: sizes.fontSize,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {task.subject || 'Untitled Task'}
          </div>
        </div>
        {dueStr ? (
          <div style={{
            flexShrink: 0,
            textAlign: 'right',
            fontSize: 'var(--font-size-xs)',
            color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)',
            whiteSpace: 'nowrap'
          }}
          >
            {status === 'completed' ? 'Completed' : `Due ${dueStr}`}
          </div>
        ) : null}
      </div>
    </div>
  )
}

WoSfTaskRow.propTypes = {
  task: PropTypes.object.isRequired,
  sizes: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired
}

function WoSfTaskListInner ({ config, sizes }) {
  const { tasks, notLinked, isLoading, error } = useSfClientTasks()
  const [selectedTaskId, setSelectedTaskId] = useState(null)
  const lastTaskRef = useRef(null)

  const handleCloseDetail = useCallback(() => setSelectedTaskId(null), [])

  const title = config?.title ?? 'Action Items'
  const icon = config?.icon ?? null

  const selectedTask = tasks.find(t => t.id === selectedTaskId) || null
  const modalTask = selectedTask || lastTaskRef.current
  if (selectedTask) {
    lastTaskRef.current = selectedTask
  }

  if (isLoading) {
    return <Loading />
  }

  if (error) {
    return (
      <FadeIn>
        <FlexRow
          className='justify-center items-center'
          style={{ minHeight: sizes.minHeight, color: 'var(--color-decline-500)', fontSize: 'var(--font-size-xs)' }}
        >
          Failed to load tasks
        </FlexRow>
      </FadeIn>
    )
  }

  if (notLinked) {
    return (
      <FadeIn>
        <FlexRow
          className='justify-center items-center'
          style={{ minHeight: sizes.minHeight, color: 'var(--text-color-subtle)', fontSize: 'var(--font-size-xs)' }}
        >
          No tasks available
        </FlexRow>
      </FadeIn>
    )
  }

  const displayTasks = applyFilters(tasks, config)

  return (
    <FadeIn>
      <div style={{ padding: '8px 12px' }}>
        <FlexRow style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {icon ? (
              <div style={{ display: 'flex', alignItems: 'center', fill: 'var(--color-primary-500)' }}>
                <Icon name={icon} customSize='24px' />
              </div>
            ) : null}
            <div style={{ fontSize: sizes.titleFontSize, fontWeight: 600 }}>
              {title}
            </div>
          </div>
          <SeDebugControl />
        </FlexRow>

        {!displayTasks.length
          ? (
            <FlexRow
              className='justify-center items-center'
              style={{
                minHeight: '80px',
                color: 'var(--text-color-subtle)',
                fontSize: 'var(--font-size-sm)'
              }}
            >
              No tasks found
            </FlexRow>
          )
          : displayTasks.map(task => (
            <WoSfTaskRow
              key={task.id}
              task={task}
              sizes={sizes}
              onClick={() => setSelectedTaskId(task.id)}
            />
          ))}

        <WoSfTaskDetailModal
          open={!!selectedTaskId}
          onClose={handleCloseDetail}
          task={modalTask}
        />
      </div>
    </FadeIn>
  )
}

WoSfTaskListInner.propTypes = {
  config: PropTypes.object,
  sizes: PropTypes.object.isRequired
}

function WoSfTaskListRenderer ({ config, styleVariant }) {
  const sizes = SIZE_STYLES[styleVariant] || SIZE_STYLES.md

  return (
    <SeDebugProvider config={config}>
      <div style={{ minHeight: sizes.minHeight }}>
        <WoSfTaskListInner
          config={config}
          sizes={sizes}
        />
      </div>
    </SeDebugProvider>
  )
}

WoSfTaskListRenderer.propTypes = {
  config: PropTypes.object,
  styleVariant: PropTypes.string
}

export default WoSfTaskListRenderer
